import { useMemo } from 'react';
import { applyEdits, buildClips, listMoments, reelStats, type Moment } from '../core/clips';
import { parseList, parseVeo, type ParseResult } from '../core/parsers';
import { periodsNeeded } from '../core/timeline';
import { useApp } from './store';

/** Everything derived from the project: parsed list, which kickoffs are needed, and the final reel. */
export function useReel() {
  const project = useApp((s) => s.project);
  const info = useApp((s) => s.info);

  const parsed: ParseResult = useMemo(() => {
    if (project.method !== 'list') return { moments: [], skipped: [], ignored: [], duplicates: 0, periodCounts: {} };
    return project.source === 'veo' ? parseVeo(project.text) : parseList(project.text, project.timeBase);
  }, [project.method, project.source, project.text, project.timeBase]);

  const usesClock = project.method === 'list' && (project.source === 'veo' || project.timeBase === 'match');
  const needed = useMemo(() => (usesClock ? periodsNeeded(parsed.moments.map((m) => m.period)) : []), [usesClock, parsed]);
  const missingKickoffs = needed.filter((p) => project.kickoffs[p] === undefined);

  const reel = useMemo(() => {
    const { moments, unmapped } = listMoments(parsed, {
      timeBase: project.source === 'veo' ? 'match' : project.timeBase,
      sync: { kickoffs: project.kickoffs, clockAt: project.clockAt },
      defaultLength: project.defaultLength,
    });
    const extra: Moment[] = project.marks.map((m) => ({ id: m.id, origin: m.origin, start: m.start, end: m.end }));
    const all = project.method === 'list' ? [...moments, ...extra] : extra;
    const clips = buildClips(all, project.settings);
    const final = applyEdits(clips, project.edits, info?.duration ?? null);
    return { clips: final, unmapped, stats: reelStats(final) };
  }, [parsed, project.source, project.timeBase, project.kickoffs, project.clockAt, project.defaultLength, project.marks, project.method, project.settings, project.edits, info?.duration]);

  return { parsed, needed, missingKickoffs, usesClock, ...reel };
}
